<template>
  <div class="pm-editor" :class="{ 'is-readonly': readonly }">
    <EditorToolbar
      v-if="!readonly && view && editorState"
      :view="view"
      :state="editorState"
      :can-upload-image="!!lessonNoteId"
    />
    <div ref="hostEl" class="pm-editor__host" />
    <EditorBubbleMenu
      v-if="!readonly"
      :view="view"
      :rect="bubble.rect"
      :visible="bubble.visible"
    />
    <EditorSlashMenu
      v-if="!readonly"
      ref="slashMenuRef"
      :view="view"
      :active="slash.active"
      :query="slash.query"
      :from="slash.from"
      :to="slash.to"
      :rect="slash.rect"
      @dismiss="dismissSlash"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Native ProseMirror editor — the canonical lesson-note editor surface.
 *
 * Public API (kept stable so existing consumers keep working):
 *  - v-model:modelValue : ProseMirror JSON doc, or a legacy string (markdown
 *                         / plain text) — converted on input.
 *  - readonly?: boolean : disables editing and hides the bubble/slash menus.
 *  - placeholder?: string
 *  - lessonNoteId?: string | number | null : enables image upload through the
 *                         existing `attachResourceToLessonNote` mutation. If
 *                         absent, paste/drop of images shows a toast and
 *                         aborts.
 *
 * Architecture: ./editor/* contains the schema, input rules, keymap, history,
 * placeholder, bubble menu plugin (+ Vue component), slash menu plugin
 * (+ Vue component), image upload plugin, and the markdown -> doc helper.
 */
import {
  ref, shallowRef, markRaw, watch, onMounted, onBeforeUnmount, reactive, type PropType,
} from 'vue';
import { useToast } from 'primevue/usetoast';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import { tableEditing, columnResizing, fixTables } from 'prosemirror-tables';
import { Node as ProseNode } from 'prosemirror-model';
import EditorBubbleMenu from './editor/EditorBubbleMenu.vue';
import EditorSlashMenu from './editor/EditorSlashMenu.vue';
import EditorToolbar from './editor/EditorToolbar.vue';
import { pmSchema, EMPTY_DOC_JSON } from './editor/pmSchema';
import { buildInputRules } from './editor/pmInputRules';
import { buildKeymap } from './editor/pmKeymap';
import { buildHistory } from './editor/pmHistory';
import { buildPlaceholderPlugin } from './editor/pmPlaceholderPlugin';
import { buildBubbleMenuPlugin } from './editor/pmBubbleMenuPlugin';
import { buildSlashMenuPlugin, type SlashState } from './editor/pmSlashMenuPlugin';
import { buildImageUploadPlugin } from './editor/pmImageUploadPlugin';
import { markdownToDoc } from './editor/pmMarkdownImport';
import {
  EducationalResourceAccessLevel,
  EducationalResourceType,
} from '@/types/composition-functions';
import {
  useAttachResourceToLessonNoteMutation,
  LessonNoteDocument,
  LessonNoteResourcesDocument,
} from '@/types/vue-apollo-helpers';

type ProseDoc = Record<string, unknown> | null;

const props = defineProps({
  modelValue: {
    type: [Object, String, null] as unknown as PropType<ProseDoc | string | null>,
    default: null,
  },
  readonly: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Start writing… press / for blocks' },
  lessonNoteId: { type: [String, Number, null] as PropType<string | number | null>, default: null },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: ProseDoc): void
  (e: 'blur'): void
  (e: 'focus'): void
}>();

const toast = useToast();

const hostEl = ref<HTMLElement | null>(null);
// CRITICAL: EditorView and EditorState must NEVER be wrapped in a Vue
// reactive proxy. Vue's proxy intercepts property access and corrupts
// ProseMirror's internal identity comparisons (specifically the
// `tr.before.eq(this.doc)` check inside `state.apply(tr)`), producing
// the "Applying a mismatched transaction" error on routine toolbar
// commands like Bold. Use `shallowRef` so .value isn't recursively
// reactified, then `markRaw` the actual instance for belt-and-braces in
// case the ref leaks through a prop or other reactivity boundary. This
// is the well-known Vue 3 ↔ ProseMirror integration trap.
const view = shallowRef<EditorView | null>(null);
const slashMenuRef = ref<InstanceType<typeof EditorSlashMenu> | null>(null);
const editorState = shallowRef<EditorState | null>(null);
// Cache the JSON string we just emitted via update:modelValue so that when
// the parent's v-model bounces it back into our `modelValue` watch we can
// recognise our own value and skip the (expensive, racy) state rebuild.
// Without this, every keystroke / toolbar command triggers a self-feedback
// rebuild that invalidates any transaction PM has already queued for the
// next DOM mutation — manifesting as "Applying a mismatched transaction
// (steps:1, docChanged:true)" and lost toolbar clicks.
let lastEmittedJson: string | null = null;

const bubble = reactive<{ visible: boolean, rect: DOMRect | null }>({
  visible: false, rect: null,
});

const slash = reactive<SlashState>({
  active: false, query: '', from: 0, to: 0, rect: null,
});

function dismissSlash() {
  slash.active = false;
  slash.query = '';
  slash.rect = null;
}

// ----------------------------------------------------------------------------
// Image upload mutation — set up at setup time (required by vue-apollo).
// In readonly mode this is dormant; in edit mode each successful upload also
// triggers a refetch of the parent `lessonNote` + `lessonNoteResources`
// queries so the rail picks up the new resource row.
// ----------------------------------------------------------------------------
const { mutate: attachResource } = useAttachResourceToLessonNoteMutation(() => {
  const id = props.lessonNoteId;
  if (id === null || id === undefined || id === '') return {};
  return {
    refetchQueries: [
      { query: LessonNoteDocument, variables: { id: String(id) } },
      { query: LessonNoteResourcesDocument, variables: { lessonNoteId: String(id) } },
    ],
  };
});

async function uploadImage(file: File): Promise<string | null> {
  const id = props.lessonNoteId;
  if (id === null || id === undefined || id === '') return null;
  const result = await attachResource({
    input: {
      lessonNoteId: String(id),
      file,
      title: file.name,
      resourceType: EducationalResourceType.Image,
      accessLevel: EducationalResourceAccessLevel.Students,
    },
  });
  const url = result?.data?.attachResourceToLessonNote?.file?.url ?? null;
  return url ?? null;
}

// ----------------------------------------------------------------------------
// Coerce incoming v-model into a real PM JSON doc shape.
// ----------------------------------------------------------------------------
function coerce(value: ProseDoc | string | null): Record<string, unknown> {
  if (!value) return { ...EMPTY_DOC_JSON };
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return { ...EMPTY_DOC_JSON };
    // Try markdown first — falls back to wrap-each-line if it can't parse.
    return markdownToDoc(value);
  }
  if (typeof value === 'object' && (value as Record<string, unknown>).type === 'doc') {
    return value as Record<string, unknown>;
  }
  return { ...EMPTY_DOC_JSON };
}

function jsonFromDoc(doc: ProseNode): Record<string, unknown> {
  return doc.toJSON() as Record<string, unknown>;
}

// ----------------------------------------------------------------------------
// EditorView mount / unmount
// ----------------------------------------------------------------------------
function buildPlugins() {
  const plugins = [
    buildInputRules(pmSchema),
    buildKeymap(pmSchema),
    ...buildHistory(),
    buildPlaceholderPlugin(() => props.placeholder),
    buildBubbleMenuPlugin((s) => {
      bubble.visible = s.visible;
      bubble.rect = s.rect;
    }),
    buildSlashMenuPlugin({
      onUpdate(s) {
        slash.active = s.active;
        slash.query = s.query;
        slash.from = s.from;
        slash.to = s.to;
        slash.rect = s.rect;
      },
      onKey(key) {
        return slashMenuRef.value?.handleKey(key) ?? false;
      },
    }),
    buildImageUploadPlugin({
      getLessonNoteId: () => (props.lessonNoteId ?? null),
      upload: (file) => uploadImage(file),
      onMissingId: () => {
        toast.add({
          severity: 'info',
          summary: 'Save first',
          detail: 'Save the note first, then images can be uploaded.',
          life: 3000,
        });
      },
      onError: (msg) => {
        toast.add({
          severity: 'error',
          summary: 'Image upload failed',
          detail: msg,
          life: 3000,
        });
      },
    }),
    dropCursor(),
    gapCursor(),
    columnResizing(),
    tableEditing(),
  ];
  return plugins;
}

function mountEditor() {
  if (!hostEl.value) return;
  const initialDoc = pmSchema.nodeFromJSON(coerce(props.modelValue));
  let state = EditorState.create({
    doc: initialDoc,
    schema: pmSchema,
    plugins: buildPlugins(),
  });
  // prosemirror-tables ships `fixTables` to repair malformed tables on load.
  const fix = fixTables(state);
  if (fix) state = state.apply(fix);

  // markRaw protects against Vue's reactive proxy corrupting ProseMirror's
  // internal identity checks (see comment on the shallowRef declarations).
  editorState.value = markRaw(state);
  view.value = markRaw(new EditorView(hostEl.value, {
    state,
    editable: () => !props.readonly,
    attributes: {
      class: 'pm-content',
      spellcheck: 'true',
    },
    // `this` is the EditorView this dispatchTransaction was configured on.
    // Using `this` (rather than reading `view.value`) is critical: if the
    // wrapper re-mounts and `view.value` is re-assigned to a NEW EditorView,
    // ProseMirror still calls THIS callback on the OLD view via its captured
    // closure. Reading `view.value` would then mix the new view's state with
    // the old view's transactions, producing "Applying a mismatched transaction"
    // on every DOM-selection change (which ProseMirror triggers continuously
    // while the user is highlighting text).
    dispatchTransaction(this: EditorView, tr) {
      let next;
      try {
        next = this.state.apply(tr);
      }
      catch (err) {
        if (err instanceof RangeError && /mismatched transaction/i.test(err.message)) {
          // eslint-disable-next-line no-console
          console.warn(
            '[ProseMirrorEditor] discarded a mismatched transaction',
            { steps: tr.steps.length, docChanged: tr.docChanged },
          );
          return;
        }
        throw err;
      }
      this.updateState(next);
      // Mirror to the Vue ref so the toolbar reacts to selection/mark changes.
      // Only mirror if this is still the current view (defence in depth).
      // markRaw — never let Vue reactify the EditorState.
      if (view.value === this) editorState.value = markRaw(next);
      if (tr.docChanged) {
        const json = jsonFromDoc(next.doc);
        // Cache the serialised form so the modelValue watch can recognise
        // the value bouncing back through v-model and skip the rebuild.
        lastEmittedJson = JSON.stringify(json);
        emit('update:modelValue', json);
      }
    },
    handleDOMEvents: {
      focus: () => { emit('focus'); return false; },
      blur: () => { emit('blur'); return false; },
    },
    nodeViews: {
      task_item: (node, nodeView, getPos) => {
        // NodeView with a real <input type="checkbox"> that updates the node attr.
        const dom = document.createElement('li');
        dom.dataset.type = 'task_item';
        dom.dataset.checked = node.attrs.checked ? 'true' : 'false';
        dom.className = 'pm-task-item';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = !!node.attrs.checked;
        checkbox.className = 'pm-task-checkbox';
        checkbox.contentEditable = 'false';
        checkbox.addEventListener('mousedown', (e) => e.stopPropagation());
        checkbox.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const pos = typeof getPos === 'function' ? getPos() : null;
          if (pos == null) return;
          const v = view.value;
          if (!v) return;
          const checked = !node.attrs.checked;
          v.dispatch(v.state.tr.setNodeMarkup(pos, undefined, { checked }));
        });
        dom.appendChild(checkbox);
        const contentDOM = document.createElement('div');
        contentDOM.className = 'pm-task-content';
        dom.appendChild(contentDOM);
        return {
          dom,
          contentDOM,
          update(updated) {
            if (updated.type.name !== 'task_item') return false;
            const isChecked = !!updated.attrs.checked;
            checkbox.checked = isChecked;
            dom.dataset.checked = isChecked ? 'true' : 'false';
            return true;
          },
          stopEvent(event) {
            return event.target === checkbox;
          },
          ignoreMutation() { return true; },
        };
      },
    },
  }));
}

function destroyEditor() {
  view.value?.destroy();
  view.value = null;
}

onMounted(mountEditor);
onBeforeUnmount(destroyEditor);

// External value -> editor sync. Don't fight the cursor on each keystroke.
//
// This watch fires both for genuine outside-driven updates (a new note
// loaded, a "restore version" mutation, etc.) AND for the value that *we
// ourselves* just emitted bouncing back through the parent's v-model. The
// latter is by far the more common case while typing/formatting; we must
// recognise our own value and skip the state rebuild — rebuilding while PM
// has a transaction in flight is what produces the
// "Applying a mismatched transaction (steps:1, docChanged:true)" crashes
// AND silently kills toolbar formatting commands.
watch(() => props.modelValue, (value) => {
  const v = view.value;
  if (!v) return;
  const incoming = JSON.stringify(coerce(value));
  // Bounce-back: this is the value we emitted ourselves.
  if (lastEmittedJson !== null && incoming === lastEmittedJson) {
    lastEmittedJson = null;
    return;
  }
  // Genuine outside change but identical to current doc — no-op.
  const current = JSON.stringify(jsonFromDoc(v.state.doc));
  if (current === incoming) return;
  // Real external update — rebuild the state from the new doc.
  const newDoc = pmSchema.nodeFromJSON(coerce(value));
  const next = EditorState.create({
    doc: newDoc,
    schema: pmSchema,
    plugins: v.state.plugins,
  });
  v.updateState(next);
  editorState.value = markRaw(next);
});

watch(() => props.readonly, () => {
  view.value?.setProps({ editable: () => !props.readonly });
});

// Placeholder is read via a closure inside the plugin, so any change just
// needs a no-op transaction to recompute the decoration.
watch(() => props.placeholder, () => {
  const v = view.value;
  if (!v) return;
  v.dispatch(v.state.tr);
});

defineExpose({
  focus() {
    view.value?.focus();
  },
});
</script>

<style>
/* ------------------------------------------------------------------ *
 * Container
 * ------------------------------------------------------------------ */
.pm-editor {
  position: relative;
  font-family: 'Outfit', system-ui, -apple-system, sans-serif;
  color: #111827;
  max-width: none;
}

.pm-editor .pm-content {
  outline: none;
  min-height: 240px;
  line-height: 1.7;
  font-size: 16px;
  color: #1f2937;
  padding: 4px 0;
  caret-color: #2B5740;
}

.pm-editor.is-readonly .pm-content {
  min-height: 0;
}

/* Placeholder decoration (set by pmPlaceholderPlugin) */
.pm-editor .pm-content .pm-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #c0c5cc;
  pointer-events: none;
  height: 0;
}

/* ------------------------------------------------------------------ *
 * Typography
 * ------------------------------------------------------------------ */
.pm-editor .pm-content h1,
.pm-editor .pm-content h2,
.pm-editor .pm-content h3 {
  font-family: 'Outfit', system-ui, sans-serif;
  color: #0f172a;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 1.5em;
  margin-bottom: 0.4em;
  letter-spacing: -0.01em;
}

.pm-editor .pm-content h1 { font-size: 2rem; }
.pm-editor .pm-content h2 { font-size: 1.5rem; }
.pm-editor .pm-content h3 { font-size: 1.25rem; }

.pm-editor .pm-content p {
  margin: 0.5em 0;
}

.pm-editor .pm-content a.pm-link {
  color: #2B5740;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.pm-editor .pm-content strong { font-weight: 600; }
.pm-editor .pm-content em { font-style: italic; }
.pm-editor .pm-content s { text-decoration: line-through; }
.pm-editor .pm-content u { text-decoration: underline; }

.pm-editor .pm-content code {
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 4px;
  font-size: 0.9em;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
}

.pm-editor .pm-content pre {
  background: #0f172a;
  color: #f8fafc;
  border-radius: 8px;
  padding: 14px 16px;
  overflow-x: auto;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.9em;
  line-height: 1.55;
}

.pm-editor .pm-content pre code {
  background: transparent;
  color: inherit;
  padding: 0;
}

.pm-editor .pm-content blockquote {
  border-left: 3px solid #2B5740;
  background: rgba(43, 87, 64, 0.04);
  margin: 1em 0;
  padding: 0.5em 1em;
  color: #4b5563;
  font-style: italic;
}

.pm-editor .pm-content hr {
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 2em 0;
}

/* Highlight mark */
.pm-editor .pm-content mark.pm-highlight {
  background: rgba(250, 204, 21, 0.35);
  padding: 1px 2px;
  border-radius: 2px;
}

/* ------------------------------------------------------------------ *
 * Lists
 * ------------------------------------------------------------------ */
.pm-editor .pm-content ul,
.pm-editor .pm-content ol {
  padding-left: 1.5em;
  margin: 0.5em 0;
}

.pm-editor .pm-content ul { list-style: disc; }
.pm-editor .pm-content ol { list-style: decimal; }

.pm-editor .pm-content ul ul { list-style: circle; }
.pm-editor .pm-content ul ul ul { list-style: square; }

.pm-editor .pm-content li { margin: 0.25em 0; }

/* Task list / item */
.pm-editor .pm-content ul.pm-task-list {
  list-style: none;
  padding-left: 0;
}

.pm-editor .pm-content li.pm-task-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.pm-editor .pm-content li.pm-task-item .pm-task-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid #9ca3af;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  margin-top: 6px;
}

.pm-editor .pm-content li.pm-task-item .pm-task-checkbox:checked {
  background: #2B5740;
  border-color: #2B5740;
}

.pm-editor .pm-content li.pm-task-item .pm-task-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 0;
  width: 4px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.pm-editor .pm-content li.pm-task-item .pm-task-content {
  flex: 1;
  min-width: 0;
}

.pm-editor .pm-content li.pm-task-item[data-checked='true'] .pm-task-content {
  color: #9ca3af;
  text-decoration: line-through;
}

/* ------------------------------------------------------------------ *
 * Tables
 * ------------------------------------------------------------------ */
.pm-editor .pm-content table {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
  table-layout: fixed;
  overflow: hidden;
  border-radius: 6px;
}

.pm-editor .pm-content th,
.pm-editor .pm-content td {
  border: 1px solid #e5e7eb;
  padding: 8px 10px;
  vertical-align: top;
  position: relative;
}

.pm-editor .pm-content th {
  background: rgba(43, 87, 64, 0.08);
  font-weight: 600;
  text-align: left;
}

.pm-editor .pm-content tr:nth-child(even) td {
  background: #fafafa;
}

/* Selected cell highlight (ProseMirror tables plugin) */
.pm-editor .pm-content .selectedCell:after {
  content: '';
  background: rgba(43, 87, 64, 0.16);
  inset: 0;
  position: absolute;
  pointer-events: none;
  z-index: 2;
}

/* Column resize handle (ProseMirror columnResizing plugin) */
.pm-editor .pm-content .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: rgba(43, 87, 64, 0.3);
  pointer-events: none;
}

.pm-editor .resize-cursor {
  cursor: col-resize;
}

/* ------------------------------------------------------------------ *
 * Image
 * ------------------------------------------------------------------ */
.pm-editor .pm-content img.pm-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 0.75em 0;
  position: relative;
}

.pm-editor .pm-content img.pm-image[data-loading='true'] {
  opacity: 0.55;
  filter: blur(0.5px);
  border: 1px dashed rgba(43, 87, 64, 0.4);
}

/* ------------------------------------------------------------------ *
 * Gap-cursor (ProseMirror)
 * ------------------------------------------------------------------ */
.pm-editor .pm-content .ProseMirror-gapcursor:after {
  border-color: #2B5740;
}

/* ------------------------------------------------------------------ *
 * Print
 * ------------------------------------------------------------------ *
 * When printing, hide all page chrome and show ONLY the centre column
 * of the lesson-note view (its `<div class="ln-center__inner">` is marked
 * with `pm-print-root`). The visibility:hidden / visibility:visible
 * trick lets us hide arbitrarily-nested ancestors (sidebar nav, app
 * shell, top bar, right rail) without needing to know their selectors.
 * The print root is then re-positioned absolutely so it takes over the
 * physical page surface.
 *
 * Applies on every print path (toolbar Print button, Ctrl/Cmd+P, browser
 * menu) — no class toggling required.
 * ------------------------------------------------------------------ */
@media print {
  /* Hide everything */
  body * {
    visibility: hidden !important;
  }
  /* Reveal only the print root and its descendants */
  .pm-print-root,
  .pm-print-root * {
    visibility: visible !important;
  }
  /* Re-position the print root to take over the page */
  .pm-print-root {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 24mm 20mm !important;
    background: #fff !important;
    color: #000 !important;
    font-family: 'Outfit', system-ui, -apple-system, sans-serif;
  }
  /* Hide editor chrome and overlays that would otherwise print */
  .pm-toolbar,
  .pm-toolbar__menu,
  .pm-bubble,
  .pm-slash,
  .ln-strip,
  .lesson-note-editor__ai-skeleton {
    display: none !important;
  }
  /* Make the editor content itself print-friendly */
  .pm-print-root .pm-editor {
    border: none !important;
    box-shadow: none !important;
  }
  .pm-print-root .pm-content {
    padding: 0 !important;
    font-size: 11pt;
    line-height: 1.55;
    color: #000;
  }
  .pm-print-root .ln-title {
    font-size: 24pt;
    margin: 0 0 8pt;
    color: #000;
    page-break-after: avoid;
  }
  .pm-print-root .pm-content h1,
  .pm-print-root .pm-content h2,
  .pm-print-root .pm-content h3 {
    page-break-after: avoid;
    color: #000;
  }
  .pm-print-root .pm-content blockquote {
    page-break-inside: avoid;
    border-left: 2pt solid #555;
    color: #222;
    padding-left: 10pt;
    margin-left: 0;
  }
  .pm-print-root .pm-content pre,
  .pm-print-root .pm-content code {
    background: #f3f3f3 !important;
    color: #000;
    font-family: ui-monospace, monospace;
    font-size: 9.5pt;
  }
  .pm-print-root .pm-content img {
    max-width: 100% !important;
    page-break-inside: avoid;
    height: auto !important;
  }
  .pm-print-root .pm-content table {
    border-collapse: collapse;
    page-break-inside: avoid;
    width: 100%;
  }
  .pm-print-root .pm-content table th,
  .pm-print-root .pm-content table td {
    border: 0.5pt solid #444;
    padding: 4pt 6pt;
    font-size: 10pt;
  }
  .pm-print-root .pm-content ul,
  .pm-print-root .pm-content ol {
    margin: 0 0 6pt;
    padding-left: 18pt;
  }
  /* Standard A4-ish page margins */
  @page {
    margin: 16mm;
  }
}
</style>
